import { ChangeDetectionStrategy, Component, inject, Input, OnInit, signal } from '@angular/core';
import { Mutable, SbSplitButton } from 'src/app/services/global/global-interfaces';
import { ContentCullDTO, ContentShareQuotaFullDTO } from '../../../services/api/api';
import { ModalService } from '../../../services/gui/modal/modal.service';
import { SearchSelectResult } from '../../../services/gui/modal/modal.model';
import { TranslateService } from '@ngx-translate/core';
import { SbSplitButtonItem } from '@ngx-seatback/controls';

type SplitButtonAction = 'delete';

@Component({
  selector: 'sb-comp-quotas',
  templateUrl: './quotas.component.html',
  styleUrls: ['./quotas.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuotasComponent implements OnInit {
  private readonly modalService = inject(ModalService);
  private readonly translate = inject(TranslateService);

  @Input({ required: true }) quotas!: ContentShareQuotaFullDTO[];

  // global variables
  isInModalContext: boolean = true;
  autofocusOrderNum: number = -1;

  // table wrapper variables
  loading = signal(true);

  ngOnInit(): void {
    if (!this.quotas) {
      this.quotas = [];
    } else {
      this.quotas = this.buildSplitButtons(this.quotas);
    }
    this.loading.set(false);
  }

  selectFilter(quota: ContentShareQuotaFullDTO) {
    const contentCull: ContentCullDTO = quota.contentCull;
    this.modalService
      .searchSelect({
        attributeType: 'CONTENTCULL',
        datatype: null,
        initial: contentCull ? [contentCull] : [],
        singlemode: true,
        allowEmpty: false,
        title: this.translate.instant('lang.globals.selectValueForNamedFilter', {
          name: this.translate.instant('lang.scheduleDetails.contentCull'),
        }),
      })
      .then((res: SearchSelectResult<ContentCullDTO>) => {
        if (!res.cancel) {
          if (res.values) {
            quota.contentCull = res.values[0];
            if (!quota.name) {
              quota.name = quota.contentCull?.name;
            }
          } else {
            quota.contentCull = null;
          }
        }
      })
      .catch(() => {});
  }

  checkPercentage() {
    if (!this.quotas || this.quotas.length === 0) {
      return false;
    }
    return this.quotas.reduce((percentage, quota) => percentage + quota.share, 0) !== 100;
  }

  public calculateWidth(): number {
    let width = 100;
    if (this.quotas && this.quotas.length !== 0) {
      for (const q of this.quotas) {
        if (q.share) {
          width = width - q.share;
        }
      }
    }
    return width;
  }

  addQuota() {
    const quota: Mutable<ContentShareQuotaFullDTO> = new ContentShareQuotaFullDTO();
    quota.orderNum = this.quotas.length;
    this.autofocusOrderNum = quota.orderNum;
    quota.share = this.calculateWidth();
    this.quotas.push(quota);
  }

  removeQuota(quota: ContentShareQuotaFullDTO) {
    const index = this.quotas.indexOf(quota, 0);
    if (index > -1) {
      this.quotas.splice(index, 1);
    }

    this.refreshOrderNum();
  }

  private refreshOrderNum(): void {
    this.quotas.forEach((quota: Mutable<ContentShareQuotaFullDTO>, idx) => {
      quota.orderNum = idx;
    });
  }

  buildSplitButtons<T extends { id: any }>(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      item.sbButtons.push({
        icon: 'trash',
        iconClass: 'hover:!tw-bg-sbRed-500 *:tw-text-sbRed-500',
        action: 'delete',
        tooltip: 'lang.globals.delete',
      });
    });
    return result;
  }

  resolveSplitButtonAction(quota: ContentShareQuotaFullDTO, $event: SplitButtonAction) {
    switch ($event) {
      case 'delete':
        this.removeQuota(quota);
        break;
      default:
        console.error('Missing action implementation of Splitbutton');
        break;
    }
  }
}
